import { 
    db, 
    firebase 
} from '../Config/firebase';
import { 
    collection, 
    getDocs, 
    getDoc,
    doc, 
    query, 
    where, 
    orderBy, 
    addDoc,
    updateDoc,
    deleteDoc,
    setDoc,
    serverTimestamp,
    writeBatch
} from 'firebase/firestore';
import { toast } from 'react-toastify';


export interface ChessArticle {
    id?: string;
    title: string;
    category: 'basics' | 'openings' | 'castling' | 'defense' | 'tactics' | 'endgame' | 'strategy' | 'other';
    content: string; // HTML or markdown content
    images?: string[]; // Array of image URLs (uploaded to Firebase Storage)
    youtubeLinks?: string[]; // Array of YouTube video URLs or IDs
    authorId: string;
    authorName: string;
    createdAt?: any;
    updatedAt?: any;
    tags?: string[];
    difficulty?: 'beginner' | 'intermediate' | 'advanced';
    views?: number;
    likes?: number;
}

/**
 * Get all chess articles for a service provider
 */
export const getChessArticles = async (
    serviceProviderId: string,
    filters?: {
        category?: ChessArticle['category'] | 'all';
        difficulty?: 'beginner' | 'intermediate' | 'advanced' | 'all';
        searchQuery?: string;
    }
): Promise<ChessArticle[]> => {
    try {
        const articlesRef = collection(db, `ServiceProviders/${serviceProviderId}/chesspedia`);
        
        let q = query(articlesRef);
        
        // Apply filters
        if (filters?.category && filters.category !== 'all') {
            q = query(q, where('category', '==', filters.category));
        }
        
        if (filters?.difficulty && filters.difficulty !== 'all') {
            q = query(q, where('difficulty', '==', filters.difficulty));
        }
        
        // Order by creation date (newest first)
        q = query(q, orderBy('createdAt', 'desc'));
        
        const snapshot = await getDocs(q);
        let articles = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        } as ChessArticle));
        
        // Client-side search filtering
        if (filters?.searchQuery) {
            const searchLower = filters.searchQuery.toLowerCase();
            articles = articles.filter(article => 
                article.title.toLowerCase().includes(searchLower) ||
                article.content.toLowerCase().includes(searchLower) ||
                article.tags?.some(tag => tag.toLowerCase().includes(searchLower))
            );
        }
        
        return articles;
    } catch (error) {
        console.error('Error getting chess articles:', error);
        throw error;
    }
};

/**
 * Get a single chess article by ID
 */
export const getChessArticle = async (
    serviceProviderId: string,
    articleId: string
): Promise<ChessArticle | null> => {
    try {
        const articleRef = doc(db, `ServiceProviders/${serviceProviderId}/chesspedia/${articleId}`);
        const articleDoc = await getDoc(articleRef);
        
        if (!articleDoc.exists()) {
            return null;
        }
        
        return {
            id: articleDoc.id,
            ...articleDoc.data()
        } as ChessArticle;
    } catch (error) {
        console.error('Error getting chess article:', error);
        throw error;
    }
};

/**
 * Create a new chess article
 */
export const createChessArticle = async (
    serviceProviderId: string,
    article: Omit<ChessArticle, 'id' | 'createdAt' | 'updatedAt'>
): Promise<string | null> => {
    try {
        const articlesRef = collection(db, `ServiceProviders/${serviceProviderId}/chesspedia`);
        
        const newArticle = {
            ...article,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            views: 0,
            likes: 0
        };
        
        const docRef = await addDoc(articlesRef, newArticle);
        toast.success('Article created successfully!');
        return docRef.id;
    } catch (error) {
        console.error('Error creating chess article:', error);
        toast.error('Failed to create article');
        return null;
    }
};

/**
 * Update an existing chess article
 */
export const updateChessArticle = async (
    serviceProviderId: string,
    articleId: string,
    updates: Partial<Omit<ChessArticle, 'id' | 'createdAt'>> & { updatedAt?: any }
): Promise<boolean> => {
    try {
        const articleRef = doc(db, `ServiceProviders/${serviceProviderId}/chesspedia/${articleId}`);
        
        await updateDoc(articleRef, {
            ...updates,
            updatedAt: serverTimestamp()
        });
        
        toast.success('Article updated successfully!');
        return true;
    } catch (error) {
        console.error('Error updating chess article:', error);
        toast.error('Failed to update article');
        return false;
    }
};

/**
 * Delete a chess article
 */
export const deleteChessArticle = async (
    serviceProviderId: string,
    articleId: string
): Promise<boolean> => {
    try {
        const articleRef = doc(db, `ServiceProviders/${serviceProviderId}/chesspedia/${articleId}`);
        await deleteDoc(articleRef);
        toast.success('Article deleted successfully!');
        return true;
    } catch (error) {
        console.error('Error deleting chess article:', error);
        toast.error('Failed to delete article');
        return false;
    }
};

/**
 * Increment article views
 */
export const incrementArticleViews = async (
    serviceProviderId: string,
    articleId: string
): Promise<void> => {
    try {
        const articleRef = doc(db, `ServiceProviders/${serviceProviderId}/chesspedia/${articleId}`);
        const articleDoc = await getDoc(articleRef);
        
        if (articleDoc.exists()) {
            const currentViews = articleDoc.data().views || 0;
            await updateDoc(articleRef, {
                views: currentViews + 1
            });
        }
    } catch (error) {
        console.error('Error incrementing article views:', error);
        // Don't show error toast for view tracking
    }
};

/**
 * Delete all articles (admin only)
 */
export const deleteAllArticles = async (
    serviceProviderId: string
): Promise<number> => {
    try {
        const articlesRef = collection(db, `ServiceProviders/${serviceProviderId}/chesspedia`);
        const snapshot = await getDocs(articlesRef);
        
        let count = 0;
        let batch = writeBatch(db);
        let batchCount = 0;
        
        for (const docSnapshot of snapshot.docs) {
            batch.delete(docSnapshot.ref);
            count++;
            batchCount++;
            
            // Firestore batch limit is 500
            if (batchCount >= 500) {
                await batch.commit();
                batch = writeBatch(db);
                batchCount = 0;
            }
        }
        
        // Commit remaining
        if (batchCount > 0) {
            await batch.commit();
        }
        
        toast.success(`Successfully deleted ${count} articles`);
        return count;
    } catch (error) {
        console.error('Error deleting all articles:', error);
        toast.error('Failed to delete articles');
        return 0;
    }
};

